import { AxiosDefaults, AxiosHeaderValue, AxiosInstance, AxiosRequestConfig, HeadersDefaults } from "axios";
import { Page } from "@supplier/entity";
import { CustomAxiosRequestConfig } from "@/api";
import { confirmable } from "@/utils/decorator/confirmable";

/**
 * 基础请求服务
 * E entity类型
 * V Vo类型
 * P pagingConditions类型
 * F entityForm类型 或等同于entity类型
 */
export class BaseService<E, V, P, F> {
    protected readonly service: AxiosInstance;

    protected readonly path: string;

    constructor(path: string, service: AxiosInstance, config?: Partial<CustomAxiosRequestConfig>) {
        this.service = service;
        this.path = path;
        this.service.defaults.baseURL = `${this.service.defaults.baseURL}`;
        if (config)
            this.service.defaults = { ...this.service.defaults, ...config } as unknown as Omit<AxiosDefaults, "headers"> & {
                headers: HeadersDefaults & {
                    [key: string]: AxiosHeaderValue;
                };
            };
    }

    /**
     * 主页数据
     */
    public async paging(params: P): Promise<Page<V>> {
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }

    /**
     * 下拉选框列表数据
     */
    public async selector(): Promise<Array<E>> {
        const { data } = await this.service.get(`${this.path}/selector`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async get(id: number | string): Promise<E> {
        const { data } = await this.service.get(`${this.path}/${id}`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async getInfo(id: number | string): Promise<V> {
        const { data } = await this.service.get(`${this.path}/${id}`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async getVo(id: number | string): Promise<V> {
        const { data } = await this.service.get(`${this.path}/vo/${id}`);
        return data;
    }

    /**
     * 创建实例
     * @param form
     */
    @confirmable("是否提交")
    public async create(form: F): Promise<E> {
        for (const key in form) {
            const value = form[key];
            if (value === undefined || value === null || value === "") {
                delete form[key];
            }
        }
        const { data } = await this.service.post(this.path, form, {
            loading: true
        } as AxiosRequestConfig);
        return data;
    }

    /**
     * 更新实例
     * @param id
     * @param form
     */
    @confirmable("是否提交")
    public async update(id: number | string, form: F) {
        const { data } = await this.service.put(`${this.path}/${id}`, form, {
            loading: true
        } as AxiosRequestConfig);
        return data;
    }

    /**
     * 删除实例
     * @param id
     */
    @confirmable("是否删除")
    public async delete(id: number | string) {
        const { data } = await this.service.delete(`${this.path}/${id}`);
        return data;
    }

    /**
     * 下载
     */
    public async download(id: number | string | (number | string)[], name: string = "模板"): Promise<Blob> {
        let path = `${this.path}/download/${id}`;
        if (Array.isArray(id)) {
            path = `${this.path}/download?ids=${id}`;
        }
        const data: Blob = await this.service.get(path, { responseType: "blob" });
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        return data;
    }

    // 预览
    public async preview(id: number | string | (number | string)[]): Promise<void> {
        let path = `${this.path}/download/${id}`;
        if (Array.isArray(id)) {
            path = `${this.path}/download?ids=${id}`;
        }
        const data: Blob = await this.service.get(path, { responseType: "blob" });
        const url = window.URL.createObjectURL(data);
        window.open(url, "_blank");
    }
}
